import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { Button } from '../../atoms/Button';
import { Card } from '../../atoms/Card';
import { Stack } from '../../atoms/Stack';
import { Inline } from '../../atoms/Inline';
import { Input } from '../../atoms/Input/Input';
import { Select, type SelectOption } from '../../atoms/Select/Select';
import { Text } from '../../atoms/Typography';
import { Alert } from '../../atoms/Alert';
import { LiveRegion } from '../../atoms/LiveRegion';
import { EmptyState } from '../../atoms/EmptyState';
import { Section } from '../../templates/Section';
import { LoadErrorState } from '../../feedback/LoadErrorState/LoadErrorState';
import { formatNumber, parameterLabel } from '../../measurements/measurementHistoryUtils';
import { TestSessionParameterRow } from './TestSessionParameterRow';
import { useMeasurementSessionData } from '../../../hooks/useMeasurementSessionDefaults';
import { useSubmitMeasurementSession } from '../../../hooks/useSubmitMeasurementSession';
import {
  PARAMETER_OPTIONS,
  getParameterUnitMetadata,
  type TestKitProfileResponse,
} from '../../../types/measurement';
import {
  MAX_ENTRIES,
  checkPlausibility,
  entryUnitsFor,
  profileEntryUnit,
  toLocalDateTimeInput,
} from './testSessionFormSchema';
import styles from './TestSessionForm.module.css';

export interface TestSessionFormProps {
  aquariumId: string;
  onSuccess: (sessionId: string) => void;
  onCancel: () => void;
  /** Values from a guided session: filled into their rows (rows are added when missing). */
  prefill?: ReadonlyArray<{ parameterId: string; value: string; testKitProfileId: string | null }>;
}

interface Row {
  readonly parameterId: string;
  readonly value: string;
  /** `unit:<code>` or `profile:<testKitProfileId>` — the value of the row's unit / test-kit select. */
  readonly selection: string;
  readonly lastValue: number | null;
  readonly lastMeasuredAt: string | null;
}

const KNOWN_PARAMETERS = new Map(PARAMETER_OPTIONS.map((option) => [option.id, option]));

function defaultSelection(parameterId: string): string {
  return `unit:${entryUnitsFor(parameterId)[0]}`;
}

/**
 * Test session: one shared timestamp and one row per parameter, pre-filled with the
 * tank's usual parameters, their last value and saved test kit. Rows left empty are
 * not saved; everything else is saved in one batch (POST /measurements/session).
 */
export function TestSessionForm({ aquariumId, onSuccess, onCancel, prefill }: TestSessionFormProps) {
  const { t, i18n } = useTranslation();
  const { sessionDefaults, testKitProfiles, isLoading, isError, refetch } = useMeasurementSessionData(aquariumId);
  const { mutate, isPending } = useSubmitMeasurementSession();

  const [rows, setRows] = useState<Row[]>([]);
  const [measuredAt, setMeasuredAt] = useState<string>(() => toLocalDateTimeInput(new Date()));
  const [measuredAtError, setMeasuredAtError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const initialized = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);

  const profilesByParameter = useMemo(() => {
    const map = new Map<string, TestKitProfileResponse[]>();
    for (const profile of testKitProfiles ?? []) {
      map.set(profile.parameterId, [...(map.get(profile.parameterId) ?? []), profile]);
    }
    return map;
  }, [testKitProfiles]);

  // Pre-fill once: the usual parameters of this tank that the form knows how to enter.
  useEffect(() => {
    if (initialized.current || !sessionDefaults || !testKitProfiles) return;
    initialized.current = true;
    const knownIds = new Set(testKitProfiles.map((p) => p.id));
    const base: Row[] = sessionDefaults.parameters
      .filter((p) => KNOWN_PARAMETERS.has(p.parameterId))
      .map((p) => ({
        parameterId: p.parameterId,
        value: '',
        selection:
          p.testKitProfileId && knownIds.has(p.testKitProfileId)
            ? `profile:${p.testKitProfileId}`
            : defaultSelection(p.parameterId),
        lastValue: p.lastValue,
        lastMeasuredAt: p.lastMeasuredAt,
      }));
    // A guided session's values: their kit and value win; missing parameters are added.
    for (const raw of prefill ?? []) {
      if (!KNOWN_PARAMETERS.has(raw.parameterId)) continue;
      // The value field is type="number": a decimal comma ("7,9") would show as empty.
      const entry = { ...raw, value: raw.value.trim().replace(',', '.') };
      const selection =
        entry.testKitProfileId && knownIds.has(entry.testKitProfileId) ? `profile:${entry.testKitProfileId}` : undefined;
      const index = base.findIndex((r) => r.parameterId === entry.parameterId);
      if (index >= 0) base[index] = { ...base[index]!, value: entry.value, selection: selection ?? base[index]!.selection };
      else base.push({ parameterId: entry.parameterId, value: entry.value, selection: selection ?? defaultSelection(entry.parameterId), lastValue: null, lastMeasuredAt: null });
    }
    setRows(base.slice(0, MAX_ENTRIES));
  }, [sessionDefaults, testKitProfiles, prefill]);

  const label = (parameterId: string): string =>
    parameterLabel(t, parameterId, KNOWN_PARAMETERS.get(parameterId)?.name ?? parameterId);

  const formatAge = (iso: string): string => {
    const diffMs = Math.max(0, Date.now() - new Date(iso).getTime());
    const days = Math.floor(diffMs / 86_400_000);
    if (days >= 1) return t('testSession.age.days', { count: days });
    const hours = Math.floor(diffMs / 3_600_000);
    if (hours >= 1) return t('testSession.age.hours', { count: hours });
    return t('testSession.age.justNow');
  };

  const unitOptions = (parameterId: string): SelectOption[] => [
    ...entryUnitsFor(parameterId).map((unit) => ({ value: `unit:${unit}`, label: unit })),
    ...(profilesByParameter.get(parameterId) ?? []).map((profile) => ({
      value: `profile:${profile.id}`,
      label: t('testSession.field.profileOption', { brand: profile.brand, unit: profile.unit }),
    })),
  ];

  const profileOf = (row: Row): TestKitProfileResponse | undefined =>
    row.selection.startsWith('profile:')
      ? profilesByParameter.get(row.parameterId)?.find((p) => p.id === row.selection.slice('profile:'.length))
      : undefined;

  const updateRow = (parameterId: string, patch: Partial<Row>): void => {
    setRows((current) => current.map((row) => (row.parameterId === parameterId ? { ...row, ...patch } : row)));
  };

  const addParameter = (parameterId: string): void => {
    if (!parameterId || rows.length >= MAX_ENTRIES || rows.some((r) => r.parameterId === parameterId)) return;
    setRows((current) => [
      ...current,
      { parameterId, value: '', selection: defaultSelection(parameterId), lastValue: null, lastMeasuredAt: null },
    ]);
  };

  // Enter moves to the next value field; it never submits (Save is explicit).
  const focusNextValue = (parameterId: string, event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const index = rows.findIndex((r) => r.parameterId === parameterId);
    const next = rows[index + 1];
    if (!next) return;
    formRef.current?.querySelector<HTMLInputElement>(`input[name="${next.parameterId}.value"]`)?.focus();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setFormError(null);
    const at = new Date(measuredAt);
    if (!measuredAt || Number.isNaN(at.getTime())) {
      setMeasuredAtError(t('testSession.validation.measuredAtRequired'));
      return;
    }
    setMeasuredAtError(undefined);
    const filled = rows.filter((row) => row.value.trim() !== '' && Number.isFinite(Number(row.value)));
    if (filled.length === 0) {
      setFormError(t('testSession.validation.valueRequired'));
      return;
    }
    mutate(
      {
        aquariumId,
        input: {
          measuredAt: at.toISOString(),
          entries: filled.map((row) => {
            const profile = profileOf(row);
            return {
              parameterId: row.parameterId,
              parameterName: KNOWN_PARAMETERS.get(row.parameterId)?.name ?? row.parameterId,
              value: Number(row.value),
              unit: profile ? profileEntryUnit(profile.unit) : row.selection.slice('unit:'.length),
              ...(profile ? { testKitProfileId: profile.id } : {}),
            };
          }),
        },
      },
      {
        onSuccess: (result) => {
          setAnnouncement(t('testSession.announce.success', { count: result.measurements.length }));
          onSuccess(result.sessionId);
        },
        onError: () => setFormError(t('testSession.error.saveFailed')),
      },
    );
  };

  if (isLoading) {
    return <LiveRegion>{t('testSession.announce.loading')}</LiveRegion>;
  }

  if (isError) {
    return (
      <LoadErrorState
        title={t('testSession.error.loadTitle')}
        message={t('testSession.error.loadDescription')}
        onRetry={() => void refetch()}
        retryLabel={t('testSession.error.retry')}
      />
    );
  }

  const remaining: SelectOption[] = PARAMETER_OPTIONS.filter((o) => !rows.some((r) => r.parameterId === o.id)).map(
    (o) => ({ value: o.id, label: label(o.id) }),
  );

  const addPicker =
    rows.length < MAX_ENTRIES && remaining.length > 0 ? (
      <Select
        label={t('testSession.action.addParameter')}
        placeholder={t('testSession.action.chooseParameter')}
        options={remaining}
        value=""
        onChange={(event) => addParameter(event.target.value)}
      />
    ) : null;

  return (
    <form ref={formRef} onSubmit={handleSubmit} noValidate className={styles.form}>
      <LiveRegion visuallyHidden>{announcement}</LiveRegion>
      {formError && <Alert>{formError}</Alert>}

      <Section title={t('testSession.section.timing')}>
        <Card>
          <Input
            type="datetime-local"
            label={t('testSession.field.measuredAt')}
            value={measuredAt}
            error={measuredAtError}
            onChange={(event) => setMeasuredAt(event.target.value)}
          />
        </Card>
      </Section>

      <Section title={t('testSession.section.parameters')}>
        <Stack gap="4">
          {rows.length === 0 ? (
            <EmptyState headline={t('testSession.empty.title')} description={t('testSession.empty.description')} />
          ) : (
            rows.map((row) => {
              const profile = profileOf(row);
              const meta = getParameterUnitMetadata(row.parameterId);
              const warning =
                profile && row.value.trim() !== ''
                  ? checkPlausibility(Number(row.value), profile.plausibleMin, profile.plausibleMax)
                  : null;
              return (
                <TestSessionParameterRow
                  key={row.parameterId}
                  rowId={row.parameterId}
                  parameterName={label(row.parameterId)}
                  value={row.value}
                  unitProfileId={row.selection}
                  unitProfileOptions={unitOptions(row.parameterId)}
                  lastValueHint={
                    row.lastValue !== null && row.lastMeasuredAt
                      ? t('testSession.hint.lastValue.withAge', {
                          value: formatNumber(row.lastValue, i18n.language),
                          unit: meta?.canonicalUnit ?? '',
                          age: formatAge(row.lastMeasuredAt),
                        })
                      : ''
                  }
                  plausibilityWarning={
                    warning ? t('testSession.warning.didYouMean', { value: warning.suggestion }) : undefined
                  }
                  onValueChange={(id, value) => updateRow(id, { value })}
                  onUnitChange={(id, selection) => updateRow(id, { selection })}
                  onRemove={(id) => setRows((current) => current.filter((r) => r.parameterId !== id))}
                  onValueKeyDown={focusNextValue}
                  removable={rows.length > 1}
                />
              );
            })
          )}
          {addPicker}
          {rows.length >= MAX_ENTRIES && <Text color="secondary">{t('testSession.validation.entriesMax')}</Text>}
        </Stack>
      </Section>

      <Inline gap="3" justify="end">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('testSession.cancel')}
        </Button>
        <Button type="submit" variant="primary" disabled={isPending || rows.length === 0}>
          {t('testSession.save')}
        </Button>
      </Inline>
    </form>
  );
}
